import { useEffect, useRef, useState } from 'react';

import type { MantineTheme } from '@mantine/core';
import {
  ActionIcon,
  Divider,
  Group,
  Stack,
  useMantineTheme,
} from '@mantine/core';

import { IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../../../../shared/components/UI/Badge/Badge';
import { Button } from '../../../../../shared/components/UI/Button/Button';
import { TextArea } from '../../../../../shared/components/UI/TextArea/TextArea';

import { GradientBorder } from '../../../../../shared/components/GradientBorder';
import type { AssistantThreadFilters } from '../../../../../shared/entities/AssistantThreadMessage';
import { BidResponseDocumentType } from '../../../../../shared/entities/BidResponseDocument';
import { WorkspaceDocumentType } from '../../../../../shared/entities/WorkspaceDocuments';
import {
  CheckBoxList,
  CheckboxItemSimple,
  CheckboxItemWithChildren,
  DropdownFooter,
} from '../../checkBoxList/CheckBoxList';
import { useGetVaultPropertiesQuery } from '../../vault/hooks/vaultProperty.hook';
import { usePreviousThreadFilters } from '../hooks/usePreviousThreadFilters';

type MessageInputProps = {
  onSubmit: (content: string, filters: AssistantThreadFilters) => void;
  size?: 'sm' | 'md';
  isDisabled?: boolean;
  threadId?: number;
};

export function MessageInput({
  size = 'md',
  onSubmit,
  threadId,
  isDisabled = false,
}: MessageInputProps) {
  const { t } = useTranslation(['bidResponse']);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [message, setMessage] = useState('');
  const theme = useMantineTheme();
  const sizeParams = getSizeParams(theme)[size];
  const previousThreadFilters = usePreviousThreadFilters(threadId);
  const [filters, setFilters] = useState<AssistantThreadFilters>(
    previousThreadFilters ?? {},
  );

  useEffect(() => {
    setFilters(previousThreadFilters ?? {});
  }, [previousThreadFilters]);

  const focusInput = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    textAreaRef.current?.focus();
  };

  const trimmedMessage = message.trim();

  const handleSubmit = () => {
    if (!trimmedMessage) {
      return;
    }

    onSubmit(trimmedMessage, filters);
    setMessage('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && trimmedMessage) {
      e.stopPropagation();
      handleSubmit();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!e.target.value.trim() && !trimmedMessage) {
      return;
    }

    setMessage(e.target.value);
  };

  const handleReset = () => {
    setMessage('');
  };

  return (
    <GradientBorder
      borderWidth={1.6}
      borderRadius={theme.radius.lg}
      gradient={theme.other.gradients.background1}
      wrapperProps={{
        w: '100%',
        maw: '926px',
        sx: {
          boxShadow: '0px 67.5px 101.25px 0px rgba(25, 64, 154, 0.06)',
        },
      }}
      contentProps={{
        w: '100%',
      }}
    >
      <Stack w="100%" spacing={0} {...sizeParams} onClick={focusInput}>
        <TextArea
          ref={textAreaRef}
          autoFocus
          variant="white"
          size="xs"
          fw={400}
          fs="18px"
          maxRows={10}
          disabled={isDisabled}
          placeholder={t('assistant.messageInput.placeholder')}
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          rightSection={
            !!message && (
              <ActionIcon
                variant="transparent"
                size="xs"
                c="primary.6"
                onClick={handleReset}
              >
                <IconX />
              </ActionIcon>
            )
          }
        />
        <ActionRow
          message={message}
          onSubmit={handleSubmit}
          filters={filters}
          onFiltersChange={setFilters}
        />
      </Stack>
    </GradientBorder>
  );
}

function ActionRow({
  message,
  onSubmit,
  filters,
  onFiltersChange,
}: {
  message: string;
  onSubmit: () => void;
  filters: AssistantThreadFilters;
  onFiltersChange: (filters: AssistantThreadFilters) => void;
}) {
  const { t } = useTranslation(['bidResponse']);

  return (
    <Group noWrap position="apart" pl="01">
      <Group noWrap spacing="05">
        <DocumentTypeCheckboxList
          filters={filters}
          onFiltersChange={onFiltersChange}
        />
        <PropertyValueCheckboxList
          filters={filters}
          onFiltersChange={onFiltersChange}
        />
      </Group>
      <Button size="md" disabled={!message.trim()} onClick={onSubmit}>
        {t('assistant.messageInput.cta')}
      </Button>
    </Group>
  );
}

type Size = 'sm' | 'md';
type SizeParams = {
  p: string;
  pl: string;
  pt: string;
};

const getSizeParams = (theme: MantineTheme): Record<Size, SizeParams> => ({
  sm: {
    p: theme.spacing['03'],
    pl: theme.spacing['02'],
    pt: theme.spacing['01'],
  },
  md: {
    p: theme.spacing['04'],
    pl: theme.spacing['04'],
    pt: theme.spacing['03'],
  },
});

type MessageInputCheckboxListProps = {
  filters: AssistantThreadFilters;
  onFiltersChange: (filters: AssistantThreadFilters) => void;
};

function DocumentTypeCheckboxList({
  filters,
  onFiltersChange,
}: {
  filters: AssistantThreadFilters;
  onFiltersChange: (filters: AssistantThreadFilters) => void;
}) {
  const { t } = useTranslation(['entities']);

  const workspaceDocumentTypes = Object.values(WorkspaceDocumentType)
    .map(docType => ({
      value: docType,
      label: t(`documentTypes.${docType}`),
    }))
    .filter(item => item.value !== WorkspaceDocumentType.UNKNOWN)
    .sort((a, b) => a.label.localeCompare(b.label));

  const bidResponseDocumentTypes = Object.values(BidResponseDocumentType)
    .map(docType => ({
      value: docType,
      label: t(`documentTypes.${docType}`),
    }))
    .filter(item => item.value !== BidResponseDocumentType.UNKNOWN)
    .sort((a, b) => a.label.localeCompare(b.label));

  const allDocumentTypes = [
    ...workspaceDocumentTypes,
    ...bidResponseDocumentTypes,
  ];

  const uniqueCheckBoxValues = allDocumentTypes.filter(
    (item, index, self) =>
      index === self.findIndex(t => t.value === item.value),
  );

  const valuesAsStrings = filters.documentTypes ?? [];

  const handleChange = (value: string, checked: boolean) => {
    if (checked) {
      onFiltersChange({
        ...filters,
        documentTypes: [...(filters.documentTypes ?? []), value],
      });
    } else {
      onFiltersChange({
        ...filters,
        documentTypes: filters.documentTypes?.filter(v => v !== value) ?? [],
      });
    }
  };

  const onReset = () => {
    onFiltersChange({ ...filters, documentTypes: [] });
  };

  return (
    <CheckBoxList label="Types de documents" count={valuesAsStrings.length}>
      <Stack spacing={0} mah={300} style={{ overflowY: 'auto' }}>
        {uniqueCheckBoxValues.map(item => (
          <CheckboxItemSimple
            key={item.value}
            checkBoxData={item}
            value={item.value}
            checked={filters.documentTypes?.includes(item.value) ?? false}
            onChange={handleChange}
          />
        ))}
      </Stack>
      <Divider color="gray.1" />
      <DropdownFooter onReset={onReset} />
    </CheckBoxList>
  );
}

function PropertyValueCheckboxList({
  filters,
  onFiltersChange,
}: MessageInputCheckboxListProps) {
  const { data: properties } = useGetVaultPropertiesQuery();
  const checkBoxData =
    properties.map(property => ({
      value: property.id.toString(),
      label: property.name,
      children: property.options.map(option => ({
        value: option.id.toString(),
        label: (
          <Badge
            bg={property.color}
            c="gray.8"
            pr="02"
            sx={{ textTransform: 'none', textDecoration: 'none' }}
          >
            {option.name}
          </Badge>
        ),
      })),
    })) ?? [];

  const valuesAsStrings = filters.optionIds?.map(id => id.toString()) ?? [];

  const handleChange = (value: string[]) => {
    onFiltersChange({ ...filters, optionIds: value.map(Number) });
  };

  const onReset = () => {
    onFiltersChange({ ...filters, optionIds: [] });
  };

  return (
    <CheckBoxList label="Propriétés" count={valuesAsStrings.length}>
      <Stack spacing={0} mah={300} style={{ overflowY: 'auto' }}>
        {checkBoxData.map(item => (
          <CheckboxItemWithChildren
            key={item.value}
            checkBoxData={item}
            values={valuesAsStrings}
            onChange={handleChange}
          />
        ))}
      </Stack>
      <Divider color="gray.1" />
      <DropdownFooter onReset={onReset} />
    </CheckBoxList>
  );
}
